
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

import GET_MARKETPLACE_ACCESS_DATA from "@/apollo/company/GetMarketplaceAccessData.gql";
import UPDATE_MARKETPLACE_ACCESS_TYPE from "@/apollo/company/UpdateMarketplaceAccessType.gql";
import UPDATE_MARKETPLACE_MANAGERS_ACCESS from "@/apollo/company/UpdateMarketplaceManagersAccess.gql";

import LoadingOverlay from "@/components/common/LoadingOverlay.vue";

import { Company, Manager } from "@/generated/graphql";
import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";
import FormWrapper from "@/components/form/FormWrapper.vue";
import MarketplaceAccessType from "@/data/companySettings/MarketplaceAccessType";
import DefaultRadioButton from "@/components/common/DefaultRadioButton.vue";
import UiButton from "@/components/button/ui-button.vue";
import FormGroup from "@/components/form/FormGroup.vue";
import CompanySettingsTabs from "../settings/CompanySettingsTabs.vue";
import RouteTabs from "@/entities/RouteTabs";
import RouteNames from "@/entities/RouteNames";
import TabsWithRoutes from "@/components/common/TabsWithRoutesNew.vue";
import ValidatableEmployeesMultiselect from "@/components/autocompleteMultiselects/ValidatableEmployeesMultiselect.vue";
import ManagersMultiselect from "@/components/autocompleteMultiselects/ManagersMultiselectNew.vue";

@Component({
  components: {
    ManagersMultiselect,
    ValidatableEmployeesMultiselect,
    CompanySettingsTabs,
    TabsWithRoutes,
    FormGroup,
    UiButton,
    DefaultRadioButton,
    FormWrapper,
    LoadingOverlay,
  },
  apollo: {
    companySettings: {
      query: GET_MARKETPLACE_ACCESS_DATA,
      variables() {
        return {
          id: this.company.id,
        };
      },
      update(data) {
        const marketplaceAccess = data.company_by_pk.marketplace_access_type;
        const selectedManagerIds = data.company_by_pk.managers.map((manager: Manager) => manager.id);

        this.chosenMarketplaceAccessType = marketplaceAccess;
        this.initialMarketplaceAccessType = marketplaceAccess;
        this.selectedManagersIds = marketplaceAccess === MarketplaceAccessType.SELECTED ? selectedManagerIds : [];
        this.initialSelectedManagerIds = selectedManagerIds;
      },
    },
  },
})
export default class CompanyInterfaceSettings extends Vue {
  
    @Prop({ type: String, required: true }) readonly company!: Company;
  

  processing: boolean = false;
  MarketplaceAccessType = MarketplaceAccessType;
  chosenMarketplaceAccessType: MarketplaceAccessType = MarketplaceAccessType.ALL;
  initialMarketplaceAccessType: MarketplaceAccessType = MarketplaceAccessType.ALL;
  selectedManagersIds: string[] = [];
  initialSelectedManagerIds: string[] = [];
  managerMultiselectError: boolean = false;

  get managerMultiselectDisabled(): boolean {
    return this.chosenMarketplaceAccessType !== MarketplaceAccessType.SELECTED;
  }

  updateCrumb() {
    const breadcrubmData = document.querySelector(
      ".site_breadcumb_ul li > span"
    );
    if (breadcrubmData) {
       breadcrubmData.classList.remove('font-semibold');
      breadcrubmData.classList.add('fontNormal');
    }
  }
  @Watch("companyId", { immediate: true, deep: true })
  crumbdata() {
    this.updateCrumb();
  }
  @Watch("$route", { immediate: true, deep: true })
  onUrlChange() {
    this.updateCrumb();
  }

 get routeTabs(): RouteTabs {
    return {
      [RouteNames.COMPANY_INTERFACE_SETTINGS]: `Marketplace Permissions`,
      [RouteNames.COMPANY_MARKETPLACE_ACCESS_REQUESTS]: `Resource Permissions`,
    };
  }

  get managerMultiselectConditions(): Record<string, any> {
    return {
      _or: [
        {
          status: {
            _neq: "SUSPENDED"
          },
        },
        {
          status: {
            _is_null: true
          },
        },
      ],
    };
  }

  get radioButtonGroupName(): string {
    return "company_access_type";
  }

  get radioButtonClasses(): string {
    return "flex items-center py-1.5 pl-2 space-x-1.5 text-sm";
  }

  get isLoading(): boolean {
    return this.$apollo.queries.companySettings.loading;
  }

  @Watch("selectedManagersIds")
  onSelectedIdsChange(selectedManagersIds: string[]): void {
    if (this.managerMultiselectError) {
      this.managerMultiselectError = selectedManagersIds.length === 0;
    }
  }

  @Watch("chosenMarketplaceAccessType")
  onChosenMarketplaceAccessTypeChange() {
    this.managerMultiselectError = false;
  }

  isChecked(value: string): boolean {
    return this.chosenMarketplaceAccessType === value;
  }

  async update(): Promise<void> {
    const managerSelected = this.selectedManagersIds.length > 0;
    if (!managerSelected && !this.managerMultiselectDisabled) {
      this.managerMultiselectError = true;
      return;
    }

    this.processing = true;

    try {
      await this.updateMarketplaceAccess();

      window.location.reload();
      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage("Updated successfully")
      );
    } catch (err) {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.errorMessage("Something went wrong")
      );
    } finally {
      this.processing = false;
    }
  }

  private updateMarketplaceAccess() {
    if (this.chosenMarketplaceAccessType !== MarketplaceAccessType.SELECTED) {
      const managerParams = this.chosenMarketplaceAccessType === MarketplaceAccessType.ALL ? {
        marketplace_access_allowed: true,
        marketplace_access_requested: false,
      } : {
        marketplace_access_allowed: false,
      };

      return this.$apollo.mutate({
        mutation: UPDATE_MARKETPLACE_ACCESS_TYPE,
        variables: {
          companyId: this.company.id,
          marketplaceAccessType: this.chosenMarketplaceAccessType,
          managerParams,
        },
      });
    }

    const deletedManagerIds = this.initialSelectedManagerIds
      .filter(managerId => !this.selectedManagersIds
        .some(selectedId => selectedId === managerId));

    const addedManagerIds = this.selectedManagersIds
      .filter(managerId => !this.initialSelectedManagerIds
        .some(selectedId => selectedId === managerId));

    return this.$apollo.mutate({
      mutation: UPDATE_MARKETPLACE_MANAGERS_ACCESS,
      variables: {
        companyId: this.company.id,
        deletedManagerIds,
        addedManagerIds,
      },
    });
  }
}
